import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiBlobUrl, apiForm, apiRaw } from './http';
import { RenderSettingsSchema, type RenderSettings } from '../lib/settings';
import { downloadBlob } from '../lib/download';

// ── schemas ─────────────────────────────────────────────────────────────────
export const MeSchema = z.object({
  user: z.object({ id: z.string(), display_name: z.string(), email: z.string() }),
  tenant_id: z.string(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  permission_labels: z.record(z.string()),
  limits: z.object({ max_upload_bytes: z.number(), max_files_per_upload: z.number(), max_input_pixels: z.number(), fidelity_min: z.number(), fidelity_max: z.number() }),
});
export type Me = z.infer<typeof MeSchema>;

export const EngineSchema = z.object({
  versions: z.object({ sharp: z.string(), vips: z.string(), mozjpeg: z.string(), webp: z.string(), imagequant: z.string(), png: z.string() }),
  metric: z.enum(['ssimulacra2', 'none']),
  metric_bin: z.string(),
  formats: z.array(z.string()),
  input_mimes: z.array(z.string()),
  png_lossless_from: z.number(),
  jobs_mode: z.enum(['inline', 'queue']),
  concurrency: z.number(),
  storage: z.enum(['S3', 'local']),
  inline_active: z.number().nullable(),
  queue: z.object({ waiting: z.number(), active: z.number() }).nullable(),
});
export type Engine = z.infer<typeof EngineSchema>;

export const AssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  mime: z.string(),
  width: z.number(),
  height: z.number(),
  size_bytes: z.number(),
  sha256: z.string(),
  has_alpha: z.boolean(),
  uploader_id: z.string(),
  uploader_name: z.string(),
  render_count: z.number(),
  created_at: z.string(),
});
export type Asset = z.infer<typeof AssetSchema>;
const AssetListSchema = z.object({ data: z.array(AssetSchema), total: z.number(), limit: z.number(), offset: z.number() });
const UploadResultSchema = z.object({ data: z.array(AssetSchema), rejected: z.array(z.object({ name: z.string(), reason: z.string() })) });

export const PresetSchema = z.object({
  id: z.string(),
  name: z.string(),
  owner_label: z.string(),
  description: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  ratio: z.string().nullable(),
  format: z.enum(['webp', 'jpeg', 'png']),
  fidelity: z.number(),
  strip: z.boolean(),
  chroma420: z.boolean(),
  sharpen: z.boolean(),
  progressive: z.boolean(),
  uses: z.number(),
  sort_order: z.number(),
  rule: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Preset = z.infer<typeof PresetSchema>;
export type PresetInput = Omit<Preset, 'id' | 'uses' | 'rule' | 'created_at' | 'updated_at'>;

export const OutputSchema = z.object({
  id: z.string(),
  format: z.string(),
  file_name: z.string(),
  size_bytes: z.number(),
  width: z.number(),
  height: z.number(),
  quality: z.number().nullable(),
  score: z.number().nullable(),
  passes: z.number(),
  metric: z.string(),
  beat_original: z.boolean(),
});
export type Output = z.infer<typeof OutputSchema>;

export const JobStatusSchema = z.enum(['queued', 'running', 'done', 'failed', 'cancelled']);
export type JobStatus = z.infer<typeof JobStatusSchema>;

export const JobSchema = z.object({
  id: z.string(),
  asset_id: z.string(),
  asset_name: z.string(),
  asset_width: z.number().nullable(),
  asset_height: z.number().nullable(),
  has_thumb: z.boolean(),
  preset_id: z.string().nullable(),
  preset_name: z.string(),
  batch_id: z.string().nullable(),
  settings: RenderSettingsSchema,
  settings_label: z.string(),
  formats: z.array(z.string()),
  fidelity: z.number(),
  status: JobStatusSchema,
  progress: z.number(),
  stage: z.string().nullable(),
  bytes_in: z.number(),
  bytes_out: z.number().nullable(),
  saving_pct: z.number().nullable(),
  passes: z.number().nullable(),
  score: z.number().nullable(),
  duration_ms: z.number().nullable(),
  error_message: z.string().nullable(),
  user_name: z.string(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  outputs: z.array(OutputSchema),
});
export type Job = z.infer<typeof JobSchema>;
const JobListSchema = z.object({ data: z.array(JobSchema), total: z.number(), limit: z.number(), offset: z.number() });

export const TotalsSchema = z.object({
  runs: z.number(), done: z.number(), failed: z.number(), queued: z.number(), running: z.number(),
  bytes_in: z.number(), bytes_out: z.number(), bytes_saved: z.number(), reduction_pct: z.number(),
  mean_score: z.number().nullable(), mean_passes: z.number().nullable(),
});
export type Totals = z.infer<typeof TotalsSchema>;

export const DashboardSchema = z.object({
  assets_prepared: z.number(),
  bytes_saved: z.number(),
  reduction_pct: z.number(),
  mean_score: z.number().nullable(),
  metric: z.enum(['ssimulacra2', 'none']),
  queue_depth: z.number(),
  concurrency: z.number(),
  library: z.object({ images: z.number(), bytes: z.number() }),
  failed: z.number(),
  recent: z.array(JobSchema),
});
export type Dashboard = z.infer<typeof DashboardSchema>;

const AddonListSchema = z.object({
  data: z.array(z.object({
    id: z.string(), name: z.string().optional(), description: z.string().optional(), icon: z.string().optional(),
    version: z.string().optional(), enabled: z.boolean(), enabled_by_default: z.boolean().optional(),
  })),
});
export type AddonRow = z.infer<typeof AddonListSchema>['data'][number];

export interface RenderRequest { asset_id: string; preset_id?: string | null; formats: string[]; settings: RenderSettings }

// ── queries ─────────────────────────────────────────────────────────────────
export const qk = {
  me: ['me'] as const,
  engine: ['engine'] as const,
  dashboard: ['dashboard'] as const,
  assets: ['assets'] as const,
  asset: (id: string) => ['asset', id] as const,
  presets: ['presets'] as const,
  renders: (key: string) => ['renders', key] as const,
  render: (id: string) => ['render', id] as const,
  totals: ['render-totals'] as const,
  queueCount: ['queue-count'] as const,
  addons: ['addons'] as const,
};

export const useMe = () => useQuery({ queryKey: qk.me, queryFn: () => api('/me', { schema: MeSchema }), staleTime: 60_000 });
export const useEngine = () => useQuery({ queryKey: qk.engine, queryFn: () => api('/engine', { schema: EngineSchema }), staleTime: 60_000 });
export const useDashboard = () => useQuery({ queryKey: qk.dashboard, queryFn: () => api('/dashboard', { schema: DashboardSchema }), refetchInterval: 10_000 });
export const useAddons = () => useQuery({ queryKey: qk.addons, queryFn: () => api('/addons', { schema: AddonListSchema }), staleTime: 30_000 });

export const useAssets = () => useQuery({ queryKey: qk.assets, queryFn: () => api('/assets?limit=200', { schema: AssetListSchema }) });
export const usePresets = () => useQuery({ queryKey: qk.presets, queryFn: () => api('/presets', { schema: z.object({ data: z.array(PresetSchema) }) }), staleTime: 60_000 });
export const useTotals = () => useQuery({ queryKey: qk.totals, queryFn: () => api('/renders/totals', { schema: TotalsSchema }) });

/** Sidebar badge: queued + running jobs. Polled slowly; the queue page polls faster on its own. */
export const useQueueCount = () =>
  useQuery({
    queryKey: qk.queueCount,
    queryFn: async () => (await api('/renders?status=queued,running&limit=1', { schema: JobListSchema })).total,
    refetchInterval: 5_000,
  });

const hasLive = (jobs: Job[] | undefined) => (jobs ?? []).some((j) => j.status === 'queued' || j.status === 'running');

export function useRenders(f: { status?: JobStatus[]; since?: string; assetId?: string; limit?: number; offset?: number }) {
  const params = new URLSearchParams();
  if (f.status?.length) params.set('status', f.status.join(','));
  if (f.since) params.set('since', f.since);
  if (f.assetId) params.set('asset_id', f.assetId);
  params.set('limit', String(f.limit ?? 50));
  params.set('offset', String(f.offset ?? 0));
  const key = params.toString();
  return useQuery({
    queryKey: qk.renders(key),
    queryFn: () => api(`/renders?${key}`, { schema: JobListSchema }),
    // Poll while anything is in flight; settle down once the list is quiet.
    refetchInterval: (q) => (hasLive(q.state.data?.data) ? 1_500 : false),
  });
}

export function useRender(id: string | null) {
  return useQuery({
    queryKey: qk.render(id ?? ''),
    queryFn: () => api(`/renders/${id}`, { schema: z.object({ data: JobSchema }) }),
    enabled: Boolean(id),
    refetchInterval: (q) => { const s = q.state.data?.data.status; return s === 'queued' || s === 'running' ? 1_000 : false; },
  });
}

// ── mutations ───────────────────────────────────────────────────────────────
function invalidateRenders(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['renders'] });
  void qc.invalidateQueries({ queryKey: qk.queueCount });
  void qc.invalidateQueries({ queryKey: qk.totals });
  void qc.invalidateQueries({ queryKey: qk.dashboard });
}

export function useUploadAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files: File[]) => {
      const form = new FormData();
      for (const f of files) form.append('files', f, f.name);
      return apiForm('/assets', form, { schema: UploadResultSchema });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.assets }); void qc.invalidateQueries({ queryKey: qk.dashboard }); },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/assets/${id}`, { method: 'DELETE' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.assets }); invalidateRenders(qc); },
  });
}

export function useCreatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PresetInput) => api('/presets', { method: 'POST', body: input, schema: z.object({ data: PresetSchema }) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.presets }); },
  });
}
export function useUpdatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & Partial<PresetInput>) => { const { id, ...body } = input; return api(`/presets/${id}`, { method: 'PATCH', body, schema: z.object({ data: PresetSchema }) }); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.presets }); },
  });
}
export function useDeletePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/presets/${id}`, { method: 'DELETE' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.presets }); },
  });
}

export function useCreateRender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RenderRequest) => api('/renders', { method: 'POST', body: input, schema: z.object({ data: JobSchema }) }),
    onSuccess: () => invalidateRenders(qc),
  });
}
export function useCreateBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: RenderRequest[]) => api('/renders/batch', { method: 'POST', body: { items }, schema: z.object({ batch_id: z.string(), data: z.array(JobSchema) }) }),
    onSuccess: () => invalidateRenders(qc),
  });
}
export function useCancelRender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/renders/${id}/cancel`, { method: 'POST', schema: z.object({ data: JobSchema }) }),
    onSuccess: () => invalidateRenders(qc),
  });
}
export function useSetAddonEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) => api(`/addons/${input.id}`, { method: 'PUT', body: { enabled: input.enabled }, schema: AddonListSchema }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.addons }); },
  });
}

// ── binary helpers ──────────────────────────────────────────────────────────
export const assetImageUrl = (id: string, kind: 'thumb' | 'preview' | 'file') => apiBlobUrl(`/assets/${id}/${kind}${kind === 'file' ? '?disposition=inline' : ''}`);

export interface PreviewResult {
  url: string;
  blob: Blob;
  bytes: number;
  originalBytes: number;
  width: number;
  height: number;
  quality: number | 'lossless' | null;
  score: number | null;
  passes: number;
  metric: string;
  metTarget: boolean;
  fileName: string;
}

/** Run the real pipeline once for the compare view and the estimate card. Nothing is stored server-side. */
export async function fetchPreview(input: { asset_id: string; format: 'webp' | 'jpeg' | 'png' | 'reference'; settings: RenderSettings }, signal?: AbortSignal): Promise<PreviewResult> {
  const res = await apiRaw('/renders/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input), signal });
  const blob = await res.blob();
  const h = (name: string) => res.headers.get(name);
  const q = h('X-Quality');
  const score = h('X-Score');
  return {
    url: URL.createObjectURL(blob),
    blob,
    bytes: Number(h('X-Output-Size') ?? blob.size),
    originalBytes: Number(h('X-Original-Size') ?? 0),
    width: Number(h('X-Output-Width') ?? 0),
    height: Number(h('X-Output-Height') ?? 0),
    quality: q === 'lossless' ? 'lossless' : q ? Number(q) : null,
    score: score ? Number(score) : null,
    passes: Number(h('X-Passes') ?? 0),
    metric: h('X-Metric') ?? 'none',
    metTarget: h('X-Met-Target') !== '0',
    fileName: h('X-File-Name') ?? 'preview',
  };
}

export async function downloadOutput(jobId: string, output: Output): Promise<void> {
  const res = await apiRaw(`/renders/${jobId}/outputs/${output.id}/file`);
  downloadBlob(await res.blob(), output.file_name);
}

export async function downloadOriginal(asset: Asset): Promise<void> {
  const res = await apiRaw(`/assets/${asset.id}/file`);
  downloadBlob(await res.blob(), asset.name);
}

export { api, apiForm, apiRaw } from './http';
export { ApiError } from './http';
